import type { PermissionContext } from '@ardoq/access-control';
import type { ComponentPreviewsState } from '@ardoq/component-overview/componentPreviews';
import { NodeModel } from '../models/types';
import {
  NavigatorConfiguration,
  PartialNavigatorState,
  LayoutBoxModel,
} from '../types';
import { NavigatorViewInterface } from '../NavigatorViewInterface';
import { classes, getCssClassFromDiffType, isMac } from '@ardoq/common-helpers';
import {
  COMPONENT_ITEM_CLASS_NAME,
  CREATE_COMPONENT_ITEM_CLASS_NAME,
  DISABLED_CLASS_NAME,
  DRAG_TARGET_CLASS_NAME,
  HIGHLIGHTED_CLASS_NAME,
  NAVIGATOR_BACKGROUND,
  NAVIGATOR_ITEM_ACTIVE_BACKGROUND,
  NAVIGATOR_ITEM_SELECTED_BACKGROUND_OPAQUE,
  NOT_SELECTABLE_CLASS_NAME,
  WORKSPACE_ITEM_CLASS_NAME,
} from '../consts';
import { ensureContrast } from '@ardoq/color-helpers';
import { CONDITIONAL_IMAGE_COLORING_CLASS_NAME } from '@ardoq/global-consts';
import { DiffType } from '@ardoq/data-model';
import { IconName } from '@ardoq/icons';
import {
  CSS_CLASS_IGNORE_LINK_TARGET,
  NavigatorDropType,
  NodeModelTypes,
  SHIFT_X,
  SHIFT_Y,
  TRANSITION_DURATION,
} from '../utils/consts';
import { DiffMode } from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { AriaAttributes, CSSProperties } from 'react';
import {
  ComponentItemProps,
  CreateComponentItemProps,
  WorkspaceItemProps,
  ScenarioItemProps,
  DragTargetProps,
  NavigatorContainerProps,
} from '../components/PropTypes';

export const stateToRenderProps = (
  state: PartialNavigatorState
): Omit<NavigatorContainerProps, 'dragElementRoot'> => {
  const {
    rootLayoutBox,
    isInDiffMode,
    isViewpointMode,
    isScenarioMode,
    tree,
    activeDiffMode,
    dropType,
    isLinking,
    eventListeners,
    navigatorViewInterface,
    navigatorConfiguration,
    isInitialized,
    dataLoadingMessage,
    dataErrorMessage,
    componentPreviews,
    permissionContext,
    isDrag,
  } = state;
  const selection = tree.getSelection();
  const isMultiselectActive = selection.length > 1;
  const canMultiselect = selection.every(node => node.hasWriteAccess);
  const i18nLocale =
    navigatorConfiguration.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;
  return {
    name:
      navigatorConfiguration.name ??
      (isScenarioMode
        ? t(
            i18nLocale,
            'componentHierarchies:navigator.scenarioName',
            'Scenario Navigator'
          )
        : t(i18nLocale, 'componentHierarchies:navigator.name', 'Navigator')),
    isMultiselectable: navigatorConfiguration.isMultiselectable ?? false,
    eventListeners,
    navigatorContentStyle: {
      height: `${rootLayoutBox.scrollHeight}px`,
      outline: 'none',
    },
    navigatorContainerClassName: classes(
      CSS_CLASS_IGNORE_LINK_TARGET,
      dropType,
      isLinking && 'is-linking',
      tree.keyboardSelectedNodeId && 'keyboard-navigation-active',
      navigatorConfiguration.useClassNameForRootComponents &&
        'navigator-container'
    ),
    dragGhostProps: getDragGhostProps(state, i18nLocale),
    parentDropTargetIndicatorStyle: getParentDropTargetIndicatorStyle(state),
    layoutBoxesContainerStyle: {
      top: `${rootLayoutBox.children[0]?.top ?? 0}px`,
    },
    actionNamespace: navigatorConfiguration.actionNamespace,
    renderProps: rootLayoutBox.children.map(layoutBoxModel =>
      layoutBoxToRenderProps({
        ...layoutBoxModel,
        selection,
        isInDiffMode,
        isViewpointMode,
        isMultiselectActive,
        canMultiselect,
        isScenarioMode,
        activeDiffMode,
        navigatorViewInterface,
        navigatorConfiguration,
        i18nLocale,
        componentPreviews,
        permissionContext,
        isDragging: isDrag,
      })
    ),
    isInitialized,
    isFilterResultEmpty: tree.hasNoFilterMatches(),
    dataLoadingMessage,
    dataErrorMessage,
    components: navigatorConfiguration.components,
    isControlledContainerHeight:
      navigatorConfiguration.isControlledContainerHeight,
    navigatorContainerId: navigatorConfiguration.useClassNameForRootComponents
      ? undefined
      : 'navigator-container',
    navigatorContentId: navigatorConfiguration.useClassNameForRootComponents
      ? undefined
      : 'navigator-content',
    navigatorContentClassName:
      navigatorConfiguration.useClassNameForRootComponents
        ? 'navigator-content'
        : undefined,
    onRootResize: navigatorConfiguration.onRootResize,
  };
};

type ToRenderPropsArgs = LayoutBoxModel & {
  selection: NodeModel[];
  isInDiffMode: boolean;
  isViewpointMode: boolean;
  isMultiselectActive: boolean;
  isScenarioMode: boolean;
  canMultiselect: boolean;
  activeDiffMode: DiffMode;
  navigatorViewInterface: NavigatorViewInterface;
  navigatorConfiguration: NavigatorConfiguration;
  i18nLocale: I18nLocale;
  componentPreviews: ComponentPreviewsState;
  permissionContext: PermissionContext | null;
  isDragging: boolean;
};

const getAriaAttributesFromNode = (
  node: NodeModel,
  i18nLocale: I18nLocale
): AriaAttributes => {
  const name = node.getName(i18nLocale);
  const hasVisibleContent = node.hasVisibleContent();
  const isExpanded = !node.isCollapsed;
  return {
    'aria-label': name,
    'aria-selected': node.isSelectedContext ? 'true' : 'false',
    'aria-level': node.getAncestors().length + 1,
    ...(hasVisibleContent && {
      'aria-expanded': isExpanded ? 'true' : 'false',
    }),
  };
};

const layoutBoxToRenderProps = (
  props: ToRenderPropsArgs
): Record<string, any> => {
  if (props.node.type === NodeModelTypes.COMPONENT) {
    return layoutBoxToComponentItemProps(props);
  }
  if (props.node.type === NodeModelTypes.WORKSPACE) {
    return layoutBoxToWorkspaceItemProps(props);
  }
  if (props.node.type === NodeModelTypes.SCENARIO) {
    return layoutBoxToScenarioItemProps(props);
  }
  if (props.node.type === NodeModelTypes.CREATE_COMPONENT) {
    return layoutBoxToCreateComponentItemProps(props);
  }
  return {};
};

const layoutBoxToComponentItemProps = ({
  node,
  left,
  classNames = [],
  selection,
  isInDiffMode,
  isMultiselectActive,
  canMultiselect,
  navigatorViewInterface,
  navigatorConfiguration: {
    eventHandlers: { onComponentContextMenu },
    showStartCreateReferenceIcon,
    shouldDimNonMatchingOptions = true,
  },
  i18nLocale,
  componentPreviews,
  permissionContext,
  isDragging,
}: ToRenderPropsArgs): ComponentItemProps => {
  const showContextMenuButton =
    Boolean(onComponentContextMenu) &&
    !(isMultiselectActive && !canMultiselect && selection.includes(node));
  const containerStyle = {
    paddingLeft: `${left}px`,
  };
  const filterExcludedClass =
    shouldDimNonMatchingOptions &&
    !node.isIncludedInContextByFilter() &&
    'excluded';
  const isHighlightedClass = node.isHighlighted && HIGHLIGHTED_CLASS_NAME;
  const notSelectableClass = !node.isSelectable && NOT_SELECTABLE_CLASS_NAME;
  const disabledClass = node.isDisabledOption && DISABLED_CLASS_NAME;
  const containerClassNames = classes(
    COMPONENT_ITEM_CLASS_NAME,
    ...classNames,
    filterExcludedClass,
    isHighlightedClass,
    notSelectableClass,
    disabledClass
  );
  const visualDiffClass = isInDiffMode
    ? getCssClassFromDiffType(node.getVisualDiffType())
    : '';
  const isLinking = classNames.includes('linking');
  const hasIncomingRef = classNames.includes('incoming');
  const hasOutgoingRef = classNames.includes('outgoing');
  const componentRepresentationData = node.getRepresentationData();

  const background = classNames.includes('selected')
    ? NAVIGATOR_ITEM_SELECTED_BACKGROUND_OPAQUE
    : classNames.includes('active')
      ? NAVIGATOR_ITEM_ACTIVE_BACKGROUND
      : NAVIGATOR_BACKGROUND;

  // to get class name with image color filter
  const nodeClassNames = componentRepresentationData?.isImage
    ? navigatorViewInterface.getComponentCssClassNames(node.id)
    : '';
  const { fill } =
    navigatorViewInterface.getComponentDisplayColorAsSVGAttributes(node.id, {
      useAsBackgroundStyle: false,
    }) ?? { fill: undefined };
  const componentColor = ensureContrast(background, fill ?? 'black');
  const iconWrapperClassNames = classes(
    'icon-container',
    'component',
    node.getModelTypeId(),
    node.id,
    visualDiffClass,
    visualDiffClass && 'visual-diff-background-circle',
    nodeClassNames,
    CONDITIONAL_IMAGE_COLORING_CLASS_NAME
  );
  const iconWrapperStyle = { fill: componentColor, color: componentColor };
  const isExternallyManaged =
    navigatorViewInterface.isComponentExternallyManaged(node.id);
  const isExternallyMissing =
    navigatorViewInterface.isComponentExternallyMissing(node.id);
  const isLocked = node.getLock();
  const isDiffTypeChanged = node.getVisualDiffType() === DiffType.CHANGED;
  const hasWriteAccess = node.hasWriteAccess;
  const linkIconName = isLinking ? IconName.NONE : IconName.REFERENCE;
  const name = node.getName();
  const aggregatedVisualDiffState = node.aggregatedVisualDiffState;
  const nodeId = node.id;
  const hasVisibleContent = node.hasVisibleContent();
  const isExpanded = !node.isCollapsed;
  const isAllSelected = node.tree?.getIsAllSelected() ?? false;
  const isSelected = node.isSelected;
  const isSelectable = node.isSelectable;
  const isDisabledOption = node.isDisabledOption;
  const modifierKey = isMac() ? '⌘' : 'Ctrl';
  const actionKey = node.isHighlighted ? 'Enter' : 'click';
  const ariaAttributes = getAriaAttributesFromNode(node, i18nLocale);
  const contextMenuAriaLabel = t(
    i18nLocale,
    'componentHierarchies:navigator.contextMenuAriaLabel',
    'Context menu'
  );

  return {
    type: NodeModelTypes.COMPONENT,
    name,
    showContextMenuButton,
    containerStyle,
    containerClassNames,
    componentRepresentationData,
    iconWrapperClassNames,
    iconWrapperStyle,
    isExternallyManaged,
    isExternallyMissing,
    isLocked,
    isDiffTypeChanged,
    hasWriteAccess,
    linkIconName,
    isInDiffMode,
    aggregatedVisualDiffState,
    nodeId,
    hasVisibleContent,
    isExpanded,
    isHighlighted: node.isHighlighted,
    hasKeyboardHighlight: Boolean(node.tree?.keyboardSelectedNodeId),
    isDragging,
    componentPreview: componentPreviews[nodeId] ?? null,
    permissionContext,
    i18nLocale,
    showStartCreateReferenceIcon,
    hasIncomingRef,
    hasOutgoingRef,
    isSelected,
    isSelectable,
    isDisabledOption,
    modifierKey,
    actionKey,
    isAllSelected,
    ariaAttributes,
    contextMenuAriaLabel,
  };
};

const layoutBoxToWorkspaceItemProps = ({
  node,
  left,
  classNames = [],
  isViewpointMode,
  isScenarioMode,
  navigatorViewInterface,
  navigatorConfiguration: { eventHandlers, showWorkspaceCloseButton },
  i18nLocale,
}: ToRenderPropsArgs): WorkspaceItemProps => {
  const containerStyle = {
    paddingLeft: `${left}px`,
  };
  const isHighlightedClass = node.isHighlighted && HIGHLIGHTED_CLASS_NAME;
  const containerClassNames = classes(
    WORKSPACE_ITEM_CLASS_NAME,
    ...classNames.filter(className => className !== DRAG_TARGET_CLASS_NAME),
    isHighlightedClass
  );
  const name = node.getName();
  const nodeId = node.id;
  const hasVisibleContent = node.hasVisibleContent();
  const isExpanded = !node.isCollapsed;
  const isExternallyManaged =
    navigatorViewInterface.isWorkspaceExternallyManaged(node.id);
  const hasCloseButton = Boolean(
    eventHandlers.onWorkspaceCloseClick &&
    (!isViewpointMode || showWorkspaceCloseButton)
  );
  const hasContextMenu =
    Boolean(eventHandlers.onWorkspaceContextmenu) && !isScenarioMode;
  const ariaAttributes = getAriaAttributesFromNode(node, i18nLocale);

  return {
    type: NodeModelTypes.WORKSPACE,
    containerStyle,
    containerClassNames,
    name,
    nodeId,
    hasVisibleContent,
    isExpanded,
    isExternallyManaged,
    hasCloseButton,
    hasContextMenu,
    ariaAttributes,
  };
};

const layoutBoxToScenarioItemProps = ({
  node,
  left,
  classNames = [],
  activeDiffMode,
  isInDiffMode,
  i18nLocale,
}: ToRenderPropsArgs): ScenarioItemProps => {
  const containerStyle = {
    paddingLeft: `${left}px`,
  };
  const containerClassNames = classes(
    'scenario-item',
    ...classNames.filter(className => className !== 'drag-target')
  );
  const nodeId = node.id;
  const hasVisibleContent = node.hasVisibleContent();
  const isExpanded = !node.isCollapsed;
  const scenarioIconName = getScenarioIconName(
    isInDiffMode ? activeDiffMode : null
  );
  const name = node.getName(i18nLocale);
  const ariaAttributes = getAriaAttributesFromNode(node, i18nLocale);

  return {
    type: NodeModelTypes.SCENARIO,
    containerStyle,
    containerClassNames,
    nodeId,
    hasVisibleContent,
    isExpanded,
    scenarioIconName,
    name,
    ariaAttributes,
  };
};

const layoutBoxToCreateComponentItemProps = ({
  node,
  left,
  classNames = [],
  i18nLocale,
}: ToRenderPropsArgs): CreateComponentItemProps => {
  const isHighlightedClass = node.isHighlighted && HIGHLIGHTED_CLASS_NAME;
  const containerClassNames = classes(
    CREATE_COMPONENT_ITEM_CLASS_NAME,
    ...classNames,
    isHighlightedClass
  );
  const name = node.getName();
  const ariaAttributes = getAriaAttributesFromNode(node, i18nLocale);
  return {
    type: NodeModelTypes.CREATE_COMPONENT,
    name,
    label: t(
      i18nLocale,
      'componentHierarchies:navigator.createComponent',
      { name },
      'Create "{{name}}"'
    ),
    nodeId: node.id,
    containerClassNames,
    containerStyle: {
      paddingLeft: `${left}px`,
    },
    ariaAttributes,
  };
};

const getScenarioIconName = (activeDiffMode: DiffMode | null) => {
  if (activeDiffMode === DiffMode.DIFF) return IconName.DIFF;
  if (activeDiffMode === DiffMode.MAIN) return IconName.MASTER;
  return IconName.SCENARIO;
};

const getDragGhostProps = (
  {
    dragPosition,
    dragTargetNode,
    isDrag,
    container,
    rootLayoutBox,
    eventListeners,
    isDragEndTransition,
    isDropHandled,
    dropTargetLayoutBox,
    selectionSize,
  }: PartialNavigatorState,
  i18nLocale: I18nLocale
): DragTargetProps => {
  const shouldShowGhost = isDrag || isDragEndTransition;
  if (!(dragTargetNode && shouldShowGhost)) {
    return {
      shouldShowGhost,
      ghostStyle: { left: '0', top: '0' },
      ghostText: '',
      targetId: '',
    };
  }

  const ghostStyle: CSSProperties = {
    left: `${dragPosition.x + SHIFT_X}px`,
    top: `${dragPosition.y + SHIFT_Y}px`,
  };
  if (isDragEndTransition) {
    ghostStyle.opacity = 0;
    ghostStyle.transition = ['opacity', 'left', 'top']
      .map(prop => `${prop} ${TRANSITION_DURATION}ms`)
      .join(', ');
    const fadeOutLayoutBox = isDropHandled
      ? dropTargetLayoutBox
      : rootLayoutBox.layoutMap.get(dragTargetNode.id);
    if (fadeOutLayoutBox) {
      const layoutBox = fadeOutLayoutBox.toViewportCoordinates(container);
      ghostStyle.left = `${layoutBox.left}px`;
      ghostStyle.top = `${layoutBox.top}px`;
    }
  }

  const onTransitionEnd = isDragEndTransition
    ? eventListeners.onTransitionEnd
    : undefined;

  const ghostText =
    selectionSize > 1
      ? t(
          i18nLocale,
          'componentHierarchies:navigator.dragGhost.components',
          { count: selectionSize },
          '{{count}} components'
        )
      : dragTargetNode.getName(i18nLocale);

  return {
    shouldShowGhost,
    ghostStyle,
    ghostText,
    onTransitionEnd,
    targetId: dragTargetNode.id,
  };
};

const getParentDropTargetIndicatorStyle = ({
  isDropTargetParent,
  dropTargetLayoutBox,
  isDropTargetBefore,
  dropType,
}: PartialNavigatorState): CSSProperties | null => {
  if (!(isDropTargetParent && dropTargetLayoutBox)) return null;
  const isNoDrop = dropType === NavigatorDropType.DROP_TYPE_NO_DROP;
  return {
    top: `${dropTargetLayoutBox.top + (isDropTargetBefore ? 0 : 26)}px`,
    left: `${dropTargetLayoutBox.left}px`,
    opacity: `${isNoDrop ? '0.5' : '1'}`,
    cursor: `${isNoDrop ? 'no-drop' : 'pointer'}`,
  };
};
